import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, UsersThree, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

/**
 * Sélecteur d'étudiants pensé pour de gros effectifs :
 * recherche texte + filtre filière/promotion + sélectionner/désélectionner
 * tout le résultat filtré en un clic, plutôt qu'une liste de cases à cocher
 * brute qui devient inutilisable au-delà d'une trentaine d'étudiants.
 */
function StudentPicker({ students, selectedIds, onToggle, onSetSelected, idPrefix }) {
  const [search, setSearch] = useState("");
  const [filiere, setFiliere] = useState("all");
  const [promotion, setPromotion] = useState("all");

  const filieres = useMemo(() => [...new Set(students.map((s) => s.filiere).filter(Boolean))].sort(), [students]);
  const promotions = useMemo(() => [...new Set(students.map((s) => s.promotion).filter(Boolean))].sort(), [students]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      if (filiere !== "all" && s.filiere !== filiere) return false;
      if (promotion !== "all" && s.promotion !== promotion) return false;
      if (q && !(`${s.name} ${s.email}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [students, search, filiere, promotion]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selectedIds.includes(s.id));

  const selectAllFiltered = () => {
    const ids = new Set(selectedIds);
    filtered.forEach((s) => ids.add(s.id));
    onSetSelected([...ids]);
  };
  const deselectAllFiltered = () => {
    const filteredIds = new Set(filtered.map((s) => s.id));
    onSetSelected(selectedIds.filter((id) => !filteredIds.has(id)));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un étudiant (nom, email)..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`${idPrefix}-search`} />
        </div>
        {filieres.length > 0 && (
          <Select value={filiere} onValueChange={setFiliere}>
            <SelectTrigger className="w-40" data-testid={`${idPrefix}-filiere-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes filières</SelectItem>
              {filieres.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {promotions.length > 0 && (
          <Select value={promotion} onValueChange={setPromotion}>
            <SelectTrigger className="w-40" data-testid={`${idPrefix}-promotion-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes promotions</SelectItem>
              {promotions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{selectedIds.length} sélectionné(s) au total · {filtered.length} affiché(s)</span>
        <button type="button" className="font-medium text-primary hover:underline" onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered} data-testid={`${idPrefix}-select-all`}>
          {allFilteredSelected ? "Tout désélectionner (affichés)" : "Tout sélectionner (affichés)"}
        </button>
      </div>

      <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-lg border border-border p-2">
        {filtered.length === 0 && <p className="p-3 text-sm text-muted-foreground">Aucun étudiant ne correspond.</p>}
        {filtered.map((s) => (
          <label key={s.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-2.5 text-sm hover:bg-secondary/50" data-testid={`${idPrefix}-opt-${s.id}`}>
            <Checkbox checked={selectedIds.includes(s.id)} onCheckedChange={() => onToggle(s.id)} />
            <span className="font-medium">{s.name}</span>
            <span className="truncate text-muted-foreground">{s.email}</span>
            {s.filiere && <Badge variant="secondary" className="ml-auto shrink-0">{s.filiere}</Badge>}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [academic, setAcademic] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", filiere: "", promotion: "" });
  const [editId, setEditId] = useState(null);
  const [newMembers, setNewMembers] = useState([]);
  const [membersOpen, setMembersOpen] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => {
    api.get("/groups").then((r) => setGroups(r.data));
    api.get("/users?role=etudiant").then((r) => setStudents(r.data));
    api.get("/academic").then((r) => setAcademic(r.data));
  };
  useEffect(() => { load(); }, []);

  const filieres = academic.filter((a) => a.type === "filiere");
  const selectedFiliere = filieres.find((f) => f.name === form.filiere);
  const promotions = academic.filter((a) => a.type === "promotion" && (!selectedFiliere || a.parent === selectedFiliere.id));
  const setFiliere = (name) => setForm((f) => ({ ...f, filiere: name, promotion: "" }));

  const toggleNew = (id) => setNewMembers((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const resetForm = () => { setForm({ name: "", description: "", filiere: "", promotion: "" }); setNewMembers([]); setEditId(null); };

  const openEdit = (g) => {
    setForm({ name: g.name, description: g.description || "", filiere: g.filiere || "", promotion: g.promotion || "" });
    setNewMembers(g.members.map((m) => m.id));
    setEditId(g.id);
    setOpen(true);
  };

  const create = async () => {
    try {
      if (editId) {
        await api.put(`/groups/${editId}`, form);
        await api.post(`/groups/${editId}/members`, { student_ids: newMembers });
        toast.success("Groupe modifié");
      } else {
        const { data } = await api.post("/groups", form);
        if (newMembers.length > 0) {
          await api.post(`/groups/${data.id}/members`, { student_ids: newMembers });
        }
        toast.success("Groupe créé");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openMembers = (g) => { setSelected(g.members.map((m) => m.id)); setMembersOpen(g); };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const saveMembers = async () => {
    try { await api.post(`/groups/${membersOpen.id}/members`, { student_ids: selected }); toast.success("Membres mis à jour"); setMembersOpen(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/groups/${id}`); toast.success("Groupe supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Groupes</h1>
          <p className="text-sm text-muted-foreground">Créez des groupes et affectez-y des étudiants. Les professeurs donnent ensuite accès à leurs cours aux groupes.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-group-btn"><Plus size={18} className="mr-2" /> Nouveau groupe</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifier le groupe" : "Créer un groupe"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label>Filière</Label>
                  <Select value={form.filiere || "__none"} onValueChange={(v) => setFiliere(v === "__none" ? "" : v)}>
                    <SelectTrigger data-testid="group-filiere"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucune</SelectItem>
                      {filieres.map((f) => <SelectItem key={f.id} value={f.name}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {filieres.length === 0 && <p className="mt-1 text-xs text-muted-foreground">Aucune filière définie — voir Gestion académique.</p>}
                </div>
                <div className="flex-1">
                  <Label>Promotion</Label>
                  <Select value={form.promotion || "__none"} onValueChange={(v) => setForm({ ...form, promotion: v === "__none" ? "" : v })} disabled={!selectedFiliere}>
                    <SelectTrigger data-testid="group-promotion"><SelectValue placeholder={selectedFiliere ? "Aucune" : "Choisir une filière d'abord"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucune</SelectItem>
                      {promotions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Étudiants du groupe</Label>
                <StudentPicker students={students} selectedIds={newMembers} onToggle={toggleNew} onSetSelected={setNewMembers} idPrefix="new-member" />
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-primary hover:bg-primary/90" data-testid="save-group-btn">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {groups.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3">
            <EmptyState
              icon={UsersThree}
              title="Aucun groupe pour l'instant"
              description="Créez un groupe et affectez-y des étudiants pour organiser vos classes et restreindre l'accès aux cours."
              actionLabel="Créer un groupe"
              onAction={() => setOpen(true)}
              testId="groups-empty"
            />
          </div>
        )}
        {groups.map((g) => (
          <div key={g.id} className="rounded-xl border border-border bg-card p-5" data-testid={`group-card-${g.id}`}>
            <div className="mb-2 flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><UsersThree size={22} weight="duotone" /></div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(g)} data-testid={`edit-group-${g.id}`}><PencilSimple size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(g.id)} data-testid={`del-group-${g.id}`}><Trash size={16} className="text-accent" /></Button>
              </div>
            </div>
            <h3 className="font-display text-lg font-bold">{g.name}</h3>
            <p className="text-sm text-muted-foreground">{g.description}</p>
            <div className="mt-3 flex flex-wrap gap-1">
              <Badge variant="secondary">{g.member_count} étudiant(s)</Badge>
              {g.filiere && <Badge variant="secondary">{g.filiere}</Badge>}
            </div>
            <Button variant="outline" size="sm" className="mt-4 w-full" onClick={() => openMembers(g)} data-testid={`members-${g.id}`}>
              <UsersThree size={14} className="mr-2" /> Gérer les étudiants
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!membersOpen} onOpenChange={(o) => !o && setMembersOpen(null)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
          <DialogHeader><DialogTitle>Membres — {membersOpen?.name}</DialogTitle></DialogHeader>
          <StudentPicker students={students} selectedIds={selected} onToggle={toggle} onSetSelected={setSelected} idPrefix="member" />
          <DialogFooter><Button onClick={saveMembers} className="bg-primary hover:bg-primary/90" data-testid="save-members-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
